import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Rocket, CheckCircle, Clock, AlertCircle, TrendingUp, FileText, Users, DollarSign, Package, Loader2, RefreshCw, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { getAuthHeaders } from '../utils/auth';
import Layout from '../components/Layout';
import { useVisibility } from '../hooks/useVisibility';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AutonomousInventionDashboard = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const isVisible = useVisibility();

  const fetchDashboard = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API}/inventions-os/dashboard/${id}`,
        { headers: getAuthHeaders() }
      );
      setDashboard(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      if (!dashboard) toast.error('Failed to load invention');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchDashboard();
    if (!isVisible) return undefined;
    const interval = setInterval(fetchDashboard, 15000);
    return () => clearInterval(interval);
  }, [fetchDashboard, isVisible]);

  const generateBOM = async () => {
    setActionLoading('bom');
    try {
      await axios.post(`${API}/inventions-os/bom/generate`, 
        { invention_id: id }, 
        { headers: getAuthHeaders(), timeout: 90000 }
      );
      toast.success('BOM generated!');
      fetchDashboard();
    } catch (e) {
      toast.error('BOM generation failed — try again');
    } finally {
      setActionLoading('');
    }
  };

  const findSuppliers = async () => {
    setActionLoading('suppliers');
    try {
      await axios.post(`${API}/inventions-os/suppliers/find`, 
        { invention_id: id }, 
        { headers: getAuthHeaders(), timeout: 90000 }
      );
      toast.success('Suppliers found!');
      fetchDashboard();
    } catch (e) {
      toast.error('Supplier search failed — try again');
    } finally {
      setActionLoading('');
    }
  };

  const sendRFQs = async () => {
    setActionLoading('rfqs');
    try {
      await axios.post(`${API}/inventions-os/rfqs/send`, 
        { invention_id: id }, 
        { headers: getAuthHeaders() }
      );
      toast.success('RFQs sent!');
      fetchDashboard();
    } catch (e) {
      toast.error('Failed to send RFQs');
    } finally {
      setActionLoading('');
    }
  };

  const regenerateSpec = async () => {
    setActionLoading('spec');
    try {
      await axios.post(`${API}/inventions-os/spec/generate`, 
        { invention_id: id }, 
        { headers: getAuthHeaders(), timeout: 90000 }
      );
      toast.success('Spec regenerated!');
      fetchDashboard();
    } catch (e) {
      toast.error('Spec generation failed');
    } finally {
      setActionLoading('');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-amber-500 mx-auto mb-4" />
            <p className="text-zinc-500">Loading invention...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!dashboard) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <p className="text-zinc-400 mb-4">Invention not found</p>
            <Button onClick={() => navigate('/inventions')} variant="outline">Back to Inventions</Button>
          </div>
        </div>
      </Layout>
    );
  }

  const inv = dashboard.invention;
  const prog = dashboard.progress;

  const getProgress = () => {
    let p = 10;
    if (prog.has_spec) p += 20;
    if (prog.has_bom) p += 20;
    if (prog.supplier_count > 0) p += 20;
    if (prog.rfq_count > 0) p += 15;
    if (prog.active_negotiations > 0) p += 15;
    return p;
  };

  const progress = getProgress();

  const getNextAction = () => {
    if (!prog.has_spec) return { label: 'Generating Spec...', action: null };
    if (!prog.has_bom) return { label: 'Generate BOM', action: generateBOM, key: 'bom' };
    if (prog.supplier_count === 0) return { label: 'Find Suppliers', action: findSuppliers, key: 'suppliers' };
    if (prog.rfq_count === 0) return { label: 'Send RFQs', action: sendRFQs, key: 'rfqs' };
    return { label: 'In Negotiation', action: null };
  };

  const nextAction = getNextAction();

  const renderMarkdown = (text) => {
    if (!text) return null;
    return text.split('\n').map((line, i) => {
      if (line.startsWith('## ')) return <h2 key={i} className="text-lg font-bold text-white mt-4 mb-2">{line.replace('## ', '')}</h2>;
      if (line.startsWith('### ')) return <h3 key={i} className="text-base font-bold text-amber-400 mt-3 mb-1">{line.replace('### ', '')}</h3>;
      if (line.startsWith('- ')) return <p key={i} className="text-sm text-zinc-300 ml-4 my-0.5">&#8226; {line.replace('- ', '')}</p>;
      if (line.trim() === '') return <br key={i} />;
      let html = line;
      html = html.replace(/\*\*(.*?)\*\*/g, '<strong class="text-white">$1</strong>');
      return <p key={i} className="text-sm text-zinc-400 my-0.5 leading-relaxed" dangerouslySetInnerHTML={{ __html: html }} />;
    });
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <Button variant="ghost" onClick={() => navigate('/inventions')} className="mb-4 text-zinc-500 hover:text-white p-0">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Inventions
          </Button>
          
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl">
                <Rocket className="w-8 h-8 text-amber-500" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white">{inv.title}</h1>
                <p className="text-zinc-500 mt-1 max-w-xl">{inv.raw_prompt}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={fetchDashboard} variant="outline" size="sm" className="gap-2">
                <RefreshCw className="w-3 h-3" /> Refresh
              </Button>
              {nextAction.action && (
                <Button 
                  onClick={nextAction.action} 
                  disabled={!!actionLoading}
                  className="gap-2 bg-amber-500 hover:bg-amber-400 text-black"
                >
                  {actionLoading === nextAction.key ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Working...</>
                  ) : (
                    <><Rocket className="w-4 h-4" /> {nextAction.label}</>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Progress */}
        <Card className="bg-zinc-900/50 border-zinc-800 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-white">Progress</h3>
              <p className="text-zinc-500 text-sm mt-1">
                {!prog.has_spec ? 'Generating product specification...' :
                 !prog.has_bom ? 'Spec ready — next: generate BOM' :
                 prog.supplier_count === 0 ? 'BOM ready — next: find suppliers' :
                 prog.rfq_count === 0 ? 'Suppliers found — next: send RFQs' :
                 'In negotiation with suppliers'}
              </p>
            </div>
            <div className="text-3xl font-bold text-amber-500">{progress}%</div>
          </div>
          <Progress value={progress} className="h-2" />
          
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-6">
            {[
              { label: 'Spec', done: prog.has_spec, icon: FileText },
              { label: 'BOM', done: prog.has_bom, icon: Package },
              { label: 'Suppliers', done: prog.supplier_count > 0, count: prog.supplier_count, icon: Users },
              { label: 'RFQs', done: prog.rfq_count > 0, count: prog.rfq_count, icon: Send },
              { label: 'Negotiating', done: prog.active_negotiations > 0, count: prog.active_negotiations, icon: DollarSign },
            ].map((step, idx) => (
              <div key={idx} className={`flex items-center gap-2 p-3 rounded-lg border ${
                step.done ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-zinc-900 border-zinc-800'
              }`}>
                {step.done ? (
                  <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                ) : (
                  <Clock className="w-5 h-5 text-zinc-600 flex-shrink-0" />
                )}
                <div>
                  <p className={`text-xs font-medium ${step.done ? 'text-emerald-400' : 'text-zinc-500'}`}>{step.label}</p>
                  {step.count !== undefined && step.count > 0 && (
                    <p className="text-xs text-zinc-600">{step.count} found</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Content Tabs */}
        <Tabs defaultValue="spec" className="w-full">
          <TabsList className="bg-zinc-900/50 border border-zinc-800">
            <TabsTrigger value="spec">Specification</TabsTrigger>
            <TabsTrigger value="bom">Bill of Materials</TabsTrigger>
            <TabsTrigger value="suppliers">Suppliers ({prog.supplier_count})</TabsTrigger>
            <TabsTrigger value="rfqs">RFQs ({prog.rfq_count})</TabsTrigger>
          </TabsList>

          <TabsContent value="spec" className="mt-4">
            <Card className="bg-zinc-900/50 border-zinc-800 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-400" /> Product Specification
                </h3>
                <Button onClick={regenerateSpec} variant="outline" size="sm" disabled={!!actionLoading}>
                  {actionLoading === 'spec' ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Regenerate'}
                </Button>
              </div>
              {dashboard.spec ? (
                <div className="bg-black/30 rounded-lg p-5 border border-zinc-800">
                  {renderMarkdown(dashboard.spec.content)}
                </div>
              ) : (
                <div className="text-center py-12 text-zinc-600">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3 text-amber-500" />
                  <p>LEE is generating the product specification...</p>
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="bom" className="mt-4">
            <Card className="bg-zinc-900/50 border-zinc-800 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Package className="w-5 h-5 text-green-400" /> Bill of Materials
                </h3>
                {!dashboard.bom && prog.has_spec && (
                  <Button onClick={generateBOM} disabled={!!actionLoading} className="bg-amber-500 hover:bg-amber-400 text-black">
                    {actionLoading === 'bom' ? <><Loader2 className="w-3 h-3 animate-spin mr-2" /> Generating...</> : 'Generate BOM'}
                  </Button>
                )}
              </div>
              {dashboard.bom ? (
                <div>
                  <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                    <span className="text-amber-400 font-semibold text-sm">Estimated Total Cost: </span>
                    <span className="text-white font-bold">${dashboard.bom.total_estimated_cost?.toFixed(2)}</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-zinc-800">
                          <th className="text-left py-2 px-3 text-zinc-500 font-medium">Part</th>
                          <th className="text-left py-2 px-3 text-zinc-500 font-medium">Category</th>
                          <th className="text-right py-2 px-3 text-zinc-500 font-medium">Qty</th>
                          <th className="text-right py-2 px-3 text-zinc-500 font-medium">Unit Cost</th>
                          <th className="text-right py-2 px-3 text-zinc-500 font-medium">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dashboard.bom.items?.map((item, idx) => (
                          <tr key={idx} className="border-b border-zinc-800/50">
                            <td className="py-2 px-3">
                              <div className="text-white font-medium">{item.part_name}</div>
                              <div className="text-zinc-600 text-xs">{item.description?.substring(0, 60)}</div>
                            </td>
                            <td className="py-2 px-3 text-zinc-400">{item.category}</td>
                            <td className="py-2 px-3 text-right text-zinc-300">{item.quantity} {item.unit}</td>
                            <td className="py-2 px-3 text-right text-zinc-300">${item.estimated_cost?.toFixed(2)}</td>
                            <td className="py-2 px-3 text-right text-white font-medium">${(item.estimated_cost * item.quantity)?.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-zinc-600">
                  {prog.has_spec ? (
                    <p>Click "Generate BOM" to create a bill of materials from your spec.</p>
                  ) : (
                    <p>Waiting for specification to be generated first...</p>
                  )}
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="suppliers" className="mt-4">
            <Card className="bg-zinc-900/50 border-zinc-800 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Users className="w-5 h-5 text-purple-400" /> Suppliers
                </h3>
                {prog.has_bom && prog.supplier_count === 0 && (
                  <Button onClick={findSuppliers} disabled={!!actionLoading} className="bg-amber-500 hover:bg-amber-400 text-black">
                    {actionLoading === 'suppliers' ? <><Loader2 className="w-3 h-3 animate-spin mr-2" /> Searching...</> : 'Find Suppliers'}
                  </Button>
                )}
                {prog.supplier_count > 0 && prog.rfq_count === 0 && (
                  <Button onClick={sendRFQs} disabled={!!actionLoading} className="bg-amber-500 hover:bg-amber-400 text-black">
                    {actionLoading === 'rfqs' ? <><Loader2 className="w-3 h-3 animate-spin mr-2" /> Sending...</> : 'Send RFQs to All'}
                  </Button>
                )}
              </div>
              {dashboard.suppliers?.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {dashboard.suppliers.map((s, idx) => (
                    <div key={idx} className="bg-black/30 p-4 rounded-lg border border-zinc-800">
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="text-white font-semibold">{s.company_name}</h4>
                        <span className="text-amber-400 text-sm font-bold">{s.rating}/5</span>
                      </div>
                      <p className="text-zinc-500 text-xs mb-2">{s.location}</p>
                      <p className="text-zinc-400 text-sm mb-3">{s.specialization}</p>
                      <div className="flex gap-3 text-xs text-zinc-500">
                        <span>MOQ: {s.moq}</span>
                        <span>Lead: {s.lead_time_days}d</span>
                        <span className="text-emerald-400">~${s.estimated_unit_cost}/unit</span>
                      </div>
                      {s.certifications?.length > 0 && (
                        <div className="flex gap-1 mt-2 flex-wrap">
                          {s.certifications.map((c, ci) => (
                            <span key={ci} className="text-[10px] px-2 py-0.5 bg-zinc-800 text-zinc-400 rounded">{c}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-zinc-600">
                  {prog.has_bom ? (
                    <p>Click "Find Suppliers" to search for manufacturers.</p>
                  ) : (
                    <p>Generate a BOM first to find relevant suppliers.</p>
                  )}
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="rfqs" className="mt-4">
            <Card className="bg-zinc-900/50 border-zinc-800 p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Send className="w-5 h-5 text-yellow-400" /> Request for Quotations
              </h3>
              {dashboard.rfqs?.length > 0 ? (
                <div className="space-y-3">
                  {dashboard.rfqs.map((rfq, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-black/30 p-4 rounded-lg border border-zinc-800">
                      <div>
                        <p className="text-white font-medium">{rfq.supplier_name}</p>
                        <p className="text-zinc-500 text-xs">{rfq.bom_items} BOM items included</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-zinc-500">{new Date(rfq.sent_at).toLocaleDateString()}</span>
                        <span className={`px-3 py-1 text-xs rounded-full ${
                          rfq.status === 'sent' ? 'bg-blue-500/20 text-blue-300' :
                          rfq.status === 'responded' ? 'bg-green-500/20 text-green-300' :
                          'bg-zinc-700 text-zinc-400'
                        }`}>
                          {rfq.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-zinc-600">
                  {prog.supplier_count > 0 ? (
                    <p>Click "Send RFQs to All" to request quotes from suppliers.</p>
                  ) : (
                    <p>Find suppliers first before sending RFQs.</p>
                  )}
                </div>
              )}
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default AutonomousInventionDashboard;
